import type { BadgeVariant } from '@/components/data-display/Badge';

// Currency-aware amount formatter, using Intl.NumberFormat when the currency
// code is a valid ISO code and falling back to a plain decimal layout otherwise.
export function formatAmount(amount: string, currencyCode: string): string {
  const numeric = Number(amount);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(numeric) ? numeric : 0);
  } catch {
    return `${currencyCode} ${(Number.isFinite(numeric) ? numeric : 0).toFixed(2)}`;
  }
}

// `received_date` and similar fields arrive as bare YYYY-MM-DD strings that
// `new Date()` interprets as UTC midnight, producing an off-by-one shift in
// timezones west of UTC. For date-only inputs, parse to a local Date so the
// rendered day matches what the operator typed.
export function formatDate(iso: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB');
}

// Maps an incoming payment status code to its Badge variant. Shared between
// IncomingList (status column) and IncomingDetailModal (header badge).
export const STATUS_BADGE: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
};
